import { orgAPI } from "@/api/api";
import { isApiError, isZenseiApiErrorBody } from "@/api/utils";
import { ROUTES } from "@/constants/routes";
import { ToolConfirmationNotificationsProvider } from "@/hooks/useToolConfirmationNotifications";
import { userPresenceColor } from "@/lib/liveblocks/userPresenceColor";
import {
  AGENT_USER_ID,
  AGENT_USER_NAME,
} from "@/lib/liveblocks/useUserDisplayName";
import { queryClient } from "@/queryClient";
import { LiveblocksProvider } from "@liveblocks/react";
import type { PropsWithChildren } from "react";

/** Room ids the backend mints for canvases (`canvas:<uuid>`). */
const CANVAS_ROOM_ID = /^canvas:([0-9a-f-]{36})$/;

/**
 * Wraps LiveblocksProvider so it can obtain access tokens from
 * `POST /api/v2/org/:organization_id/user/:user_id/liveblocks-token` (room in
 * JSON body) via the api-client.
 *
 * Auth-agnostic: this provider only depends on `orgAPI` having a bearer token
 * configured by the time `authEndpoint` is invoked. Two callers wire that up:
 *   - main app: `AuthInject` points `orgAPI` at the Auth0 token (this provider
 *     is rendered inside `Auth0ProviderWithRedirectCallback` in `App.tsx`).
 *   - canvas hero capture: `CaptureOnlyApiInject` points `orgAPI` at the
 *     short-lived capture JWT (provider is rendered without Auth0 in
 *     `canvas-capture.tsx`).
 *
 * `resolveUsers` is the canonical Liveblocks pattern for surfacing user info
 * (name / avatar / color) to default UI components like `<Cursors />`,
 * `<Thread>`, `<InboxNotification>`, etc. — they call `useUser(userId)` under
 * the hood, which dispatches into this resolver.
 *
 * We hit the same `/api/v2/org/:org/users` endpoint that powers the rest of
 * the app (members list, share dialog, etc.) and route through the global
 * `queryClient` so successive lookups share its cache. The deterministic color
 * mirrors the Go backend's `userPresenceColor`, keeping `useUser(...).color`
 * and `useOther(...).info.color` consistent.
 */
export function LiveblocksAuthProvider({ children }: PropsWithChildren) {
  // Map Zensei error envelopes to the body shape the Liveblocks browser SDK
  // expects from its `authEndpoint`:
  //   - 4xx (permanent: bad room, no permission, user gone) → return
  //     `{error, reason}` so the SDK stops its reconnect loop.
  //   - everything else (5xx, network, unexpected) → rethrow so the SDK
  //     retries with backoff.
  const authEndpoint = async (room?: string) => {
    try {
      // No room → the notification hooks (useInboxNotifications /
      // useUnreadInboxNotificationsCount) authenticating cross-room. The backend
      // treats an empty room as a request for a user-scoped notification token
      // (identifies the user + org, authorizes zero real rooms).
      return await orgAPI.liveblocks.auth({ room: room ?? "" });
    } catch (error) {
      if (isApiError(error) && error.status >= 400 && error.status < 500) {
        const reason = isZenseiApiErrorBody(error.data)
          ? (error.data.error?.message ?? "Access denied.")
          : "Access denied.";
        return { error: "forbidden", reason };
      }
      throw error;
    }
  };

  const listUsers = () =>
    queryClient.fetchQuery({
      queryKey: ["users"],
      queryFn: () => orgAPI.users.list(),
    });

  const resolveUsers = async ({
    userIds,
  }: {
    userIds: string[];
  }): Promise<
    ({ name: string; avatar: string; color: string } | undefined)[]
  > => {
    const users = await listUsers();
    const byId = new Map(users.map((u) => [u.id, u]));
    return userIds.map((id) => {
      // Before the list lookup: the agent is not in it and never will be.
      if (id === AGENT_USER_ID) {
        return {
          name: AGENT_USER_NAME,
          avatar: "/favicon.svg",
          color: "var(--rust)",
        };
      }
      const user = byId.get(id);
      if (!user) return undefined;
      return {
        name: user.name,
        avatar: user.picture_url,
        color: userPresenceColor(id),
      };
    });
  };

  // Backs the @-mention autocomplete in the comment composer. The agent is not
  // suggestible: mentioning it would imply a turn nothing dispatches yet.
  const resolveMentionSuggestions = async ({ text }: { text: string }) => {
    const users = await listUsers();
    const q = text.trim().toLowerCase();
    return users
      .filter(
        (u) =>
          !q ||
          u.name?.toLowerCase().includes(q) ||
          u.email?.toLowerCase().includes(q),
      )
      .map((u) => u.id);
  };

  // Thread inbox notifications render the room they came from. Unknown room
  // shapes resolve to an empty object rather than undefined so the notification
  // still renders without a name.
  const resolveRoomsInfo = async ({ roomIds }: { roomIds: string[] }) =>
    Promise.all(
      roomIds.map(async (roomId) => {
        const canvasId = CANVAS_ROOM_ID.exec(roomId)?.[1];
        if (!canvasId) return {};
        try {
          const canvas = await queryClient.fetchQuery({
            queryKey: ["canvas", canvasId],
            queryFn: () => orgAPI.canvas.getById(canvasId),
          });
          return {
            name: canvas.name,
            url: `${ROUTES.CANVAS.ROOT}/${canvasId}`,
          };
        } catch {
          return {};
        }
      }),
    );

  return (
    <LiveblocksProvider
      authEndpoint={authEndpoint}
      resolveUsers={resolveUsers}
      resolveMentionSuggestions={resolveMentionSuggestions}
      resolveRoomsInfo={resolveRoomsInfo}
      // Rooms held open by TabPreloadProvider (and the mounted canvas) park
      // their websockets after the browser tab has been hidden this long, then
      // auto-reconnect and resync storage on focus.
      backgroundKeepAliveTimeout={15 * 60 * 1000}
    >
      <ToolConfirmationNotificationsProvider>
        {children}
      </ToolConfirmationNotificationsProvider>
    </LiveblocksProvider>
  );
}
